import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const HopeBc = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.hope-bc:About Hope BC Paragraph 1">
          Hope, nestled in the Fraser Valley of{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a charming town celebrated for its stunning natural
          beauty and outdoor recreation. Surrounded by the majestic Coast
          Mountains and the Fraser and Coquihalla Rivers, it offers a
          picturesque backdrop for activities such as hiking, camping, fishing,
          and wildlife watching. The area boasts outdoor havens like Manning
          Park and Kawkawa Lake, making it a paradise for nature enthusiasts.
          Hope’s history, rooted in the Fraser Canyon Gold Rush, is reflected in
          attractions like the Hope Museum. The town’s cultural scene thrives
          with events like Brigade Days and Chainsaw Carving Competitions.
          Serving as a transportation hub, Hope provides access to various{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>{' '}
          through its strategic highway connections. It welcomes visitors and
          residents with its warm community spirit and natural wonders.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,200 - $2,200',
          bed2: '$1,700 - $2,700',
          bed3: '$2,000 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.hope-bc:Apartment Hunting in Hope BC Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Hope</LinkText> –
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Apartments for rent Hope BC Paragraph">
              <LinkText href={links.apartment}>
                Renting an apartment in Hope
              </LinkText>{' '}
              often means embracing a comfortable and convenient lifestyle.
              Apartments for rent in Hope range from older, character-filled
              buildings to newer developments, catering to those who value ease
              of access to town amenities, including shops and local eateries,
              as well as proximity to the surrounding natural beauty.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Houses for rent Hope BC Paragraph">
              <LinkText href={links.house}>Houses for rent in Hope</LinkText>{' '}
              are ideal for those seeking space and a closer connection to the
              area’s serene environment. These homes, varying from quaint, older
              styles to modern builds, often come with yards or gardens,
              offering a peaceful retreat and space for family activities or
              gardening enthusiasts.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Townhouses for rent Hope BC Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Hope
              </LinkText>{' '}
              provide a blend of communal and independent living. They are
              typically chosen by small families or couples looking for a
              balance between having their own space and the ease of
              maintenance, often including multiple levels and sometimes small
              outdoor areas.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Basements for rent Hope BC Paragraph">
              Basements for rent in Hope are an economical choice, offering a
              more secluded type of living. These rentals, part of larger homes,
              are popular among those looking for affordable options, such as
              young professionals or singles, and provide a cozy, if sometimes
              less light-filled, living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Condos for rent Hope BC Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Hope
              </LinkText>{' '}
              appeal to those seeking a hassle-free living experience. These
              rental units, which can range from basic to more upscale, often
              come with additional facilities like communal lounges or small
              fitness areas, appealing to retirees, young professionals, or
              small families who enjoy community living with the convenience of
              added amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Rooms for rent Hope BC Paragraph">
              <LinkText href={links.room}>Rooms for rent in Hope</LinkText> are
              the most budget-conscious option, often preferred by students,
              seasonal workers, or those seeking short-term arrangements. This
              type of rental typically involves sharing common areas like
              kitchens and living rooms with other residents, fostering a sense
              of community and an opportunity for social interaction.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Short term rentals Hope BC Paragraph">
              Hope, a small town in British Columbia, offers a modest range of
              employment opportunities, primarily in sectors like retail,
              tourism, and service industries, thanks to its location as a
              gateway to the Fraser Valley and the interior of BC. It is not
              particularly known for its academic institutions, being more of a
              community-centered town. The demand for{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Hope
              </LinkText>{' '}
              does see an increase, especially during tourist seasons given its
              proximity to outdoor recreational activities and scenic spots.
              This pattern of short-term rental popularity, more pronounced
              during peak tourist times, differs from larger cities and
              university towns where such rentals are consistently in demand,
              especially over summer and at the start of school semesters.{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                The flexibility provided by short-term rentals
              </LinkText>{' '}
              is an important consideration for renters, especially those
              looking for accommodations that align with temporary work or
              leisure visits.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-bc:Cheap rentals Hope BC Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>cheap rentals in Hope</LinkText>,
              try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Hope, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.hope-bc:Living in Hope BC Paragraph">
            Nestled in the heart of British Columbia, Hope offers a captivating
            blend of natural beauty and small-town charm. Located in the Fraser
            Valley region, this peaceful town is surrounded by majestic
            mountains, verdant forests, and pristine waters. Known for its
            welcoming and friendly community, Hope offers an abundance of
            attractions and amenities for both locals and visitors alike.
            Outdoor enthusiasts can partake in a variety of activities such as
            hiking, fishing, and boating, while those interested in culture can
            explore the local arts scene and historical landmarks. With its
            serene atmosphere and laid-back lifestyle, Hope is the perfect
            destination for a tranquil coastal getaway.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.hope-bc:Living in Hope BC Paragraph 2">
            If you’re not sure that Hope is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.hope-bc:Popular Neighbourhoods Hope BC Paragraph">
          Like any city, Hope is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Hope, Silver Creek, Kawkawa Lake, Flood Hope, and Skagit
          Valley. For more info about Hope, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.hope-bc:Local Landmarks Hope BC Paragraph">
          Hope, BC, is a town brimming with remarkable landmarks that showcase
          its natural beauty and cultural significance. One such landmark is the
          Othello Tunnels, a series of railway tunnels that have been converted
          into a picturesque walking trail. These tunnels, nestled amidst
          stunning granite cliffs, offer visitors a unique perspective on the
          region’s history and provide a memorable hiking experience. Another
          notable landmark is the Memorial Park, a beautiful green space in the
          heart of the town that features a cenotaph honoring fallen soldiers.
          This park is a peaceful retreat where visitors can relax, picnic, and
          reflect on the town’s rich heritage. For those interested in the gold
          rush era, the Yale Historic Site is a must-visit landmark. This site
          preserves the remains of the historic town of Yale, once a bustling
          hub during the gold rush, and offers guided tours and exhibits that
          bring the past to life. Lastly, the Kawkawa Lake is a natural landmark
          that is a favorite among locals and visitors alike. This stunning lake
          is surrounded by towering mountains and offers opportunities for
          swimming, fishing, and picnicking. These landmarks make Hope, BC, a
          captivating destination that appeals to both nature lovers and history
          enthusiasts, offering a unique and unforgettable experience.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default HopeBc
